import React, { MutableRefObject, useEffect, useState } from "react";

import ArrowDown from "@uui/ArrowDown";
import NotificationText from "@uui/NotificationText";
import { isEmpty, map } from "lodash/fp";

import Typography from "@mui/material/Typography";

import { IconHeadingIcon } from "@app/analytics/components/IconHeading";
import type { PulseTopic } from "@app/analytics/Pulse/types";
import useIntersectionObserver from "@app/common/hooks/useIntersectionObserver";

import Nudge from "@rui/Nudge";
import { Column, Row } from "@rui/Stack";

import EmptyTable from "./EmptyTable";
import LoadingTable from "./LoadingTable";
import { ModuleHeading } from "./styles";
import { Table } from "./TopicsTable/styles";
import TopicsTableRow from "./TopicsTable/TopicsTableRow";

const getStableTopicIdsKey = (topics: PulseTopic[]) =>
  map("id", topics).join("");

const WhatYouMissed = ({
  loading,
  topics,
  selectedRange,
}: {
  loading: boolean;
  topics: PulseTopic[];
  selectedRange: string;
}) => {
  const [expanded, setExpanded] = useState<PulseTopic["id"] | null>(null);
  const [showNudge, setShowNudge] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const { targetElementRef, isVisible, scrollIntoView } =
    useIntersectionObserver({
      threshold: 0.25,
    });

  useEffect(() => {
    if (isEmpty(topics)) return;
    setExpanded(topics[0].id);
  }, [getStableTopicIdsKey(topics)]);

  useEffect(() => {
    setHasScrolled(false);
  }, [selectedRange]);

  useEffect(() => {
    if (hasScrolled) return;

    if (isVisible) {
      setShowNudge(false);
      setHasScrolled(true);
    } else if (!loading && !isEmpty(topics)) {
      setShowNudge(true);
    }
  }, [loading, topics, isVisible, hasScrolled]);

  const toggleExpanded = (idToExpand: PulseTopic["id"]) => {
    if (idToExpand === expanded) {
      setExpanded(null);
    } else {
      setExpanded(idToExpand);
    }
  };

  const handleClickNudge = () => {
    scrollIntoView({ behavior: "smooth" });
    setShowNudge(false);
  };

  return (
    <>
      <Table
        ref={targetElementRef as MutableRefObject<HTMLDivElement | null>}
        sx={{ scrollMarginTop: "140px" }}
      >
        <ModuleHeading variant="apart" spacing={2} sx={{ px: 3 }}>
          <Row spacing={1}>
            <IconHeadingIcon type="what_you_missed" />
            <Column>
              <Typography variant="h3">What you missed</Typography>
              <Typography variant="labelReg" color="shades.neutral.500">
                Your highest engagement topics from meetings you didn't attend
              </Typography>
            </Column>
          </Row>
        </ModuleHeading>
        {loading ? (
          <LoadingTable />
        ) : isEmpty(topics) ? (
          <EmptyTable
            title="You didn't miss any meetings in this time period"
            subtext="When you miss meetings, a recap will show up here"
            Icon={NotificationText}
          />
        ) : (
          topics.map((topic) => (
            <TopicsTableRow
              key={topic.id}
              expanded={expanded === topic.id}
              onToggleExpanded={toggleExpanded}
              topic={topic}
            />
          ))
        )}
      </Table>
      {showNudge && (
        <Nudge
          label="See what you missed"
          onClick={handleClickNudge}
          startIcon={<ArrowDown />}
          sx={{ position: "fixed", top: "90%" }}
        />
      )}
    </>
  );
};

export default WhatYouMissed;
